/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

import MenuSearch from "pages/components/MenuSearch";
import DocsMenu from "pages/components/DocsMenu";
import UserMenu from "pages/components/UserMenu";
import EmailDialog from "pages/components/EmailDialog";
import HeaderTypeSwitch from "pages/components/HeaderTypeSwitch";
import ClientesPagadoresQuickViewButton from "pages/components/ClientesPagadoresQuickView";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";

import MDBox from "components/MDBox";

import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// ---------- pequenos utilitários ----------
const asNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/\./g, "").replace(",", "."); // 1.234,56 → 1234.56
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

function DashboardNavbar({ absolute, light, isMini }) {
  const theme = useTheme();

  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
    uiOverrides,
    getGradientFromColor,
    getTextColorForBackground,
    sidenavColor,
  } = controller;

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  const [openEmail, setOpenEmail] = useState(false);

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");
  }, [fixedNavbar]);

  async function syncNow() {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg("A sincronizar…");

    const passos = [
      "fabricantes",
      "fornecedores",
      "vendedores",
      "entidadesgrupos",
      "entidades",
      "setoresatividade",
      "clientespagadores",
      "vendedoresrotas",
      "materiaissabores",
      "materiaissegmentos",
      "materiaiscapacidades",
      "materiaisfamilias",
      "materiaismarcas",
      "materiaisgrupos",
      "materiaissubfamilias",
      "materiaissubmarcas",
      "materiaistipos",
      "materiaisembalagens",
      "materiaisunidades",
      "materiais",
      "materiaisstocks",
      "faturascompras",
      "faturasvendas",
    ];

    try {
      for (const ep of passos) {
        setSyncMsg(`A sincronizar: ${ep}…`);
        const resp = await fetch(`/api/sincronizacao/${ep}/run`, { method: "POST" });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.message || `Falha em ${ep}`);
      }
      setSyncMsg("Sincronização concluída ✅");
    } catch (err) {
      setSyncMsg(`Erro: ${err?.message || "Falha na sincronização"}`);
    } finally {
      setTimeout(() => setSyncing(false), 800);
    }
  }

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  // --- Auto texto para navbar baseado no próprio navbarBg (nome do tema ou HEX)
  const getNavbarAutoTextColor = () => {
    const bg = uiOverrides?.navbarBg;
    if (!bg) return undefined;
    const g = theme.palette.gradients?.[bg];
    const main = g ? g.main : bg; // se for HEX, usa o HEX diretamente
    return getTextColorForBackground(main); // "#FFFFFF" ou "#000000"
  };

  const resolvedNavbarText =
    uiOverrides?.navbarText !== null ? uiOverrides.navbarText : getNavbarAutoTextColor();

  // Breadcrumbs precisa da prop 'light' para mudar o esquema interno (white vs dark)
  const breadcrumbsLight = resolvedNavbarText
    ? resolvedNavbarText.toLowerCase() === "#ffffff"
    : light;

  // cor real do "círculo" do user (usa a paleta do tema p/ a cor ativa da sidenav)
  const userIconColor = theme.palette?.[sidenavColor]?.main || theme.palette.info.main;

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(th) => {
        const base = navbar(th, { transparentNavbar, absolute, light, darkMode });
        if (!uiOverrides?.navbarBg) return base;

        return {
          ...base,
          background: getGradientFromColor(th, uiOverrides.navbarBg),
          color: resolvedNavbarText || "inherit",
        };
      }}
    >
      <Toolbar sx={(th) => navbarContainer(th)}>
        <MDBox
          color={resolvedNavbarText || "inherit"}
          mb={{ xs: 1, md: 0 }}
          sx={(th) => navbarRow(th, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={breadcrumbsLight}
          />
        </MDBox>

        {isMini ? null : (
          <MDBox sx={(th) => navbarRow(th, { isMini })} color={resolvedNavbarText || "inherit"}>
            {/* === BLOCO RESPONSIVO (Pesquisa + Ícones) === */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1, md: 2 }}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ width: "100%" }}
            >
              {/* Pesquisa: full-width em mobile */}
              <MDBox sx={{ flexGrow: 1 }}>
                <MenuSearch size="sm" fullWidth maxSuggestions={100} />
              </MDBox>

              {/* Fila única de ícones */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  "& .MuiIconButton-root": { width: 36, height: 36 },
                }}
              >
                <Tooltip title="Enviar mensagem">
                  <IconButton
                    size="small"
                    onClick={() => setOpenEmail(true)}
                    color="inherit"
                    sx={{ "&:hover": { bgcolor: "rgba(255,152,0,0.10)" } }}
                  >
                    <Icon>mail</Icon>
                  </IconButton>
                </Tooltip>

                {/* <DocsMenu /> */}
                <HeaderTypeSwitch />
                {/* <ClientesPagadoresQuickViewButton /> */}

                <Tooltip title={syncMsg || "Forçar sincronização agora"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={syncNow}
                      disabled={syncing}
                      sx={{ ml: 1 }}
                    >
                      <Icon>{syncing ? "hourglass_top" : "autorenew"}</Icon>
                    </IconButton>
                  </span>
                </Tooltip>

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                  aria-label="Abrir/fechar menu lateral"
                >
                  <Icon fontSize="medium">{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>

                {!uiOverrides?.hideConfigurator && (
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleConfiguratorOpen}
                    aria-label="Configurações"
                  >
                    <Icon>settings</Icon>
                  </IconButton>
                )}

                <UserMenu />
              </Stack>
            </Stack>

            <EmailDialog
              maxEachMB={18}
              maxTotalMB={20}
              open={openEmail}
              onClose={() => setOpenEmail(false)}
            />
            {renderMenu()}
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = { absolute: false, light: false, isMini: false };
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};
export default DashboardNavbar;
